import React from "react";
import { Helmet } from "react-helmet";
import shop from "../assets/newshot.jpg";
import ownerImage from "../assets/1OwnerPic.jpg";
import { PageShell } from "./ui/PageShell";
import { Eyebrow } from "./ui/Eyebrow";

const cardBase =
  "rounded-xl border border-slate-200 bg-white shadow-sm";

const bulletDot = "mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600";

const About = () => {
  return (
    <>
      {/* React Helmet SEO */}
      <Helmet>
        <title>
          About Advanced Automotive Smog | Temecula Auto Repair Shop
        </title>

        <meta
          name="description"
          content="Advanced Automotive Smog is a Temecula repair shop offering complete diagnostics and repair for European, luxury, and everyday vehicles, plus smog and emissions testing."
        />
        <meta
          name="keywords"
          content="Temecula auto repair shop, European car repair Temecula, Land Rover Jaguar Mercedes repair, vehicle diagnostics Temecula, smog check Temecula"
        />

        {/* Open Graph Metadata (Facebook, LinkedIn) */}
        <meta
          property="og:title"
          content="About Advanced Automotive Smog | Temecula Auto Repair Shop"
        />
        <meta
          property="og:description"
          content="Advanced Automotive Smog is a Temecula repair shop offering complete diagnostics and repair for European, luxury, and everyday vehicles, plus smog and emissions testing."
        />
        <meta
          property="og:image"
          content="https://www.advancedautomotivesmog.com/assets/about.jpeg"
        />
        <meta
          property="og:url"
          content="https://www.advancedautomotivesmog.com/about"
        />
        <meta property="og:type" content="website" />

        {/* Twitter Metadata */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="About Advanced Automotive Smog | Temecula Auto Repair Shop"
        />
        <meta
          name="twitter:description"
          content="A Temecula repair shop offering complete diagnostics and repair for European, luxury, and everyday vehicles, plus smog and emissions testing."
        />
        <meta
          name="twitter:image"
          content="https://www.advancedautomotivesmog.com/assets/about.jpeg"
        />

        {/* Canonical URL */}
        <link
          rel="canonical"
          href="https://www.advancedautomotivesmog.com/about"
        />
      </Helmet>

      <PageShell className="bg-[#F8F9FA] text-slate-700">
        {/* Hero / intro */}
        <header className="border-b border-slate-200 bg-[#EEF2F7]">
          <div className="mx-auto max-w-6xl border-l-4 border-slate-300 px-4 pb-10 pt-7 text-center sm:px-5 md:px-8 md:pt-8">
          <div className="mx-auto max-w-3xl">
            <Eyebrow tone="light" className="mb-4">
              Advanced Automotive · About
            </Eyebrow>
            <h1 className="mb-4 text-[1.65rem] font-semibold leading-snug tracking-tight text-navy-900 sm:text-3xl md:text-4xl">
              The story behind{" "}
              <span className="text-teal-600">Advanced Automotive Smog</span>.
            </h1>
            <p className="text-sm font-normal leading-relaxed text-slate-500 md:text-base">
              From a small Temecula shop to a trusted name for high-end smog
              checks and diagnostics, our focus has always been the same: honest
              work, clean cars, and long-term relationships.
            </p>
          </div>
          </div>
        </header>

        {/* Story / experience */}
        <div className="border-t border-slate-200 bg-white py-10 md:py-14">
          <div className="mx-auto max-w-6xl border-l-4 border-slate-300 px-4 sm:px-5 md:px-8">
            {/* ========== MOBILE ========== */}
            <div className="space-y-10 md:hidden">
              <section className={`${cardBase} overflow-hidden`}>
                <div className="relative aspect-[4/3] w-full min-h-[200px] overflow-hidden sm:aspect-[16/10]">
                  <img
                    src={shop}
                    alt="Advanced Automotive Smog shop exterior"
                    className="h-full w-full object-cover object-center"
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                <div className="space-y-4 p-6">
                  <div>
                    <p className="mb-1 text-[11px] font-medium tracking-normal text-teal-600">
                      Our Story
                    </p>
                    <h2 className="text-xl font-semibold leading-snug text-navy-900">
                      Fast, honest, and eco-friendly{" "}
                      <span className="text-teal-600">smog inspections</span>.
                    </h2>
                  </div>

                  <p className="text-sm leading-relaxed text-slate-700">
                    We&apos;re a family-friendly shop in Temecula that specializes
                    in high-end brands like Land Rover, Jaguar, and Mercedes —
                    but we work on all makes and models. Our goal is simple:
                    make your visit quick, clear, and stress-free.
                  </p>

                  <div className="mt-5 grid grid-cols-3 gap-3">
                    <div className={`${cardBase} px-3 py-3 text-center`}>
                      <p className="mb-1 text-[10px] font-medium tracking-normal text-teal-600">
                        30+ Years
                      </p>
                      <p className="text-[0.75rem] text-slate-700">
                        of experience
                      </p>
                    </div>
                    <div className={`${cardBase} px-3 py-3 text-center`}>
                      <p className="mb-1 text-[10px] font-medium tracking-normal text-teal-600">
                        High-End
                      </p>
                      <p className="text-[0.75rem] text-slate-700">
                        Euro &amp; EV
                      </p>
                    </div>
                    <div className={`${cardBase} px-3 py-3 text-center`}>
                      <p className="mb-1 text-[10px] font-medium tracking-normal text-teal-600">
                        All Cars
                      </p>
                      <p className="text-[0.75rem] text-slate-700">
                        All drivers
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* ========== DESKTOP / TABLET STORY ========== */}
            <div className={`hidden md:block ${cardBase} p-8 md:p-10`}>
              <div className="mb-8 text-left">
                <h2 className="mb-2 text-3xl font-bold text-navy-900">
                  Our Story
                </h2>
                <p className="text-sm text-slate-500 md:text-base">
                  Fast, honest, and eco-friendly smog inspections, done right.
                </p>
              </div>

              <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-start lg:gap-10">
                <div className="w-full lg:w-1/2">
                  <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
                    <div className="relative aspect-[16/10] w-full min-h-[220px] overflow-hidden lg:aspect-[16/9]">
                      <img
                        src={shop}
                        alt="Advanced Automotive Smog shop exterior"
                        className="h-full w-full object-cover object-center"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </div>
                </div>

                <div className="w-full lg:w-1/2">
                  <h3 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
                    Your trusted smog experts in Temecula
                  </h3>
                  <p className="mb-4 text-sm leading-relaxed text-slate-700 md:text-base">
                    We&apos;re a small, family-friendly shop based in Temecula,
                    specializing in high-end cars such as Land Rover, Jaguar,
                    and Mercedes — but we work on all makes and models. While
                    smog checks are one of our specialties, we also offer a full
                    range of services for your vehicle, from factory maintenance
                    to key repairs.
                  </p>
                  <p className="text-sm leading-relaxed text-slate-700 md:text-base">
                    Our certified technicians are dedicated to making every visit
                    simple and stress-free. You&apos;ll always know what
                    we&apos;re doing, why we&apos;re doing it, and what&apos;s
                    best for your car in the long run.
                  </p>

                  <div className="mt-8 grid grid-cols-3 gap-4">
                    <div className={`${cardBase} px-4 py-3 text-center`}>
                      <p className="mb-1 text-xs font-medium tracking-normal text-teal-600">
                        30+ Years
                      </p>
                      <p className="text-sm text-slate-700">
                        of hands-on experience
                      </p>
                    </div>
                    <div className={`${cardBase} px-4 py-3 text-center`}>
                      <p className="mb-1 text-xs font-medium tracking-normal text-teal-600">
                        High-End
                      </p>
                      <p className="text-sm text-slate-700">
                        Land Rover, Jaguar, Mercedes &amp; more
                      </p>
                    </div>
                    <div className={`${cardBase} px-4 py-3 text-center`}>
                      <p className="mb-1 text-xs font-medium tracking-normal text-teal-600">
                        All Vehicles
                      </p>
                      <p className="text-sm text-slate-700">
                        We work on all makes &amp; models
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Certifications / pillars */}
        <div className="border-t border-slate-200 bg-[#F8F9FA] py-10 md:py-14">
          <div className="mx-auto max-w-6xl border-l-4 border-slate-300 px-4 sm:px-5 md:px-8">
          {/* Mobile pillars */}
          <section className="space-y-6 md:hidden">
            <div className={`${cardBase} p-6`}>
              <h3 className="mb-3 text-base font-semibold text-teal-600">
                Expert Technicians
              </h3>
              <ul className="space-y-2 text-[0.9rem] text-slate-700">
                <li className="flex items-start gap-2">
                  <span className={bulletDot} />
                  Certified techs who care about doing the job right.
                </li>
                <li className="flex items-start gap-2">
                  <span className={bulletDot} />
                  Clear explanations before we recommend anything.
                </li>
              </ul>
            </div>

            <div className={`${cardBase} p-6`}>
              <h3 className="mb-3 text-base font-semibold text-teal-600">
                Cutting-Edge Technology
              </h3>
              <ul className="space-y-2 text-[0.9rem] text-slate-700">
                <li className="flex items-start gap-2">
                  <span className={bulletDot} />
                  Modern diagnostic tools and smog equipment.
                </li>
                <li className="flex items-start gap-2">
                  <span className={bulletDot} />
                  Accurate readings so you know exactly where your car stands.
                </li>
              </ul>
            </div>

            <div className={`${cardBase} p-6`}>
              <h3 className="mb-3 text-base font-semibold text-teal-600">
                Eco-Friendly Practices
              </h3>
              <ul className="space-y-2 text-[0.9rem] text-slate-700">
                <li className="flex items-start gap-2">
                  <span className={bulletDot} />
                  Responsible recycling and clean-air focused work.
                </li>
                <li className="flex items-start gap-2">
                  <span className={bulletDot} />
                  Helping Temecula drivers move toward a cleaner future.
                </li>
              </ul>
            </div>
          </section>

          {/* Desktop pillars */}
          <div className="hidden md:grid md:grid-cols-3 md:gap-6">
            <div className={`${cardBase} p-6`}>
              <h3 className="mb-3 text-lg font-semibold text-teal-600">
                Expert Technicians
              </h3>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <span className={bulletDot} />
                  Certified and passionate about the work they do.
                </li>
                <li className="flex items-start gap-2">
                  <span className={bulletDot} />
                  Every vehicle is treated like our own.
                </li>
                <li className="flex items-start gap-2">
                  <span className={bulletDot} />
                  Friendly communication from start to finish.
                </li>
              </ul>
            </div>

            <div className={`${cardBase} p-6`}>
              <h3 className="mb-3 text-lg font-semibold text-teal-600">
                Cutting-Edge Technology
              </h3>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <span className={bulletDot} />
                  Latest diagnostic tools and smog equipment.
                </li>
                <li className="flex items-start gap-2">
                  <span className={bulletDot} />
                  Accurate readings and fewer surprises later on.
                </li>
                <li className="flex items-start gap-2">
                  <span className={bulletDot} />
                  Tech that makes your visit easier, not harder.
                </li>
              </ul>
            </div>

            <div className={`${cardBase} p-6`}>
              <h3 className="mb-3 text-lg font-semibold text-teal-600">
                Eco-Friendly Practices
              </h3>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <span className={bulletDot} />
                  Commitment to our community and the environment.
                </li>
                <li className="flex items-start gap-2">
                  <span className={bulletDot} />
                  Responsible recycling and reduced emissions.
                </li>
                <li className="flex items-start gap-2">
                  <span className={bulletDot} />
                  Helping drivers move toward a cleaner future.
                </li>
              </ul>
            </div>
          </div>
          </div>
        </div>

        {/* Owner + credentials — soft gray + white credential card */}
        <div className="border-t border-slate-200 bg-[#EEF2F7] py-10 pb-14 md:py-14 md:pb-20">
          <div className="mx-auto max-w-6xl border-l-4 border-slate-300 px-4 sm:px-5 md:px-8">
            {/* Mobile owner */}
            <section className={`md:hidden ${cardBase} overflow-hidden`}>
              <div className="relative aspect-[4/3] w-full min-h-[220px] overflow-hidden">
                <img
                  src={ownerImage}
                  alt="Owner of Advanced Automotive Smog"
                  className="h-full w-full object-cover object-[center_22%]"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              <div className="space-y-5 p-6">
                <div>
                  <p className="mb-1 text-[11px] font-medium tracking-normal text-teal-600">
                    My Story
                  </p>
                  <h2 className="text-xl font-semibold leading-snug text-navy-900">
                    Meet{" "}
                    <span className="text-teal-600">Edgar, the owner</span> of
                    Advanced Automotive Smog.
                  </h2>
                </div>

                <p className="text-sm leading-relaxed text-slate-700">
                  I&apos;ve spent over 30 years working with cars—from
                  diagnostics and repair to smog inspections. This shop is built
                  on simple values: honest work, respect for every customer, and
                  a high standard for every vehicle that rolls into the bay.
                </p>

                <div className={`${cardBase} p-5`}>
                  <h3 className="mb-2 text-sm font-semibold text-teal-600">
                    Credentials
                  </h3>
                  <ul className="list-inside list-disc space-y-1 text-[0.9rem] text-slate-700 marker:text-teal-600">
                    <li>ASE Master Technician</li>
                    <li>Licensed Smog &amp; Repair Technician</li>
                    <li>Factory trained by Jaguar &amp; Land Rover</li>
                    <li>Electric Vehicle approved</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Desktop owner */}
            <div className={`hidden md:block ${cardBase} p-8 md:p-10`}>
              <div className="mb-8 text-left">
                <h2 className="mb-2 text-3xl font-bold text-navy-900">
                  My Story
                </h2>
                <p className="text-sm text-slate-500 md:text-base">
                  Get to know the heart and soul behind the shop.
                </p>
              </div>

              <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-start lg:gap-10">
                <div className="w-full lg:w-1/2">
                  <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
                    <div className="relative aspect-[16/11] w-full min-h-[260px] overflow-hidden md:aspect-[4/3] lg:aspect-auto lg:min-h-[320px]">
                      <img
                        src={ownerImage}
                        alt="Owner of Advanced Automotive Smog"
                        className="h-full min-h-[260px] w-full object-cover object-[center_22%] lg:min-h-[320px]"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex w-full flex-col gap-6 lg:w-1/2">
                  <div>
                    <h3 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
                      Meet the Owner
                    </h3>
                    <p className="mb-3 text-sm leading-relaxed text-slate-700 md:text-base">
                      Hi, I&apos;m Edgar, the proud owner of Advanced Automotive
                      Smog. I&apos;ve been passionate about cars since I was a
                      kid, and I&apos;ve dedicated my career to making sure every
                      vehicle that comes through our doors gets the care and
                      attention it deserves.
                    </p>
                    <p className="text-sm leading-relaxed text-slate-700 md:text-base">
                      My goal is simple: do honest work, treat people with
                      respect, and make sure every driver leaves feeling
                      confident about their vehicle—not confused.
                    </p>
                  </div>

                  <div className={`${cardBase} p-6`}>
                    <h3 className="mb-3 text-lg font-semibold text-teal-600 sm:text-xl">
                      Credentials
                    </h3>
                    <ul className="list-inside list-disc space-y-2 text-sm text-slate-700 marker:text-teal-600 md:text-base">
                      <li className="font-semibold">ASE Master Technician</li>
                      <li className="font-semibold">
                        Licensed Smog &amp; Repair Technician
                      </li>
                      <li className="font-semibold">
                        Factory trained by Jaguar &amp; Land Rover
                      </li>
                      <li className="font-semibold">
                        Electric Vehicle approved
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </PageShell>
    </>
  );
};

export default About;
